import {
  scheduledValueAt,
  TAX_PRESETS,
  type AssetPayment,
  type CategoryId,
  type FormulaSource,
  type FormulaSpec,
  type ItemId,
  type MonthIndex,
  type TaxBracket,
  type TaxPresetContext,
  type TaxPresetId,
  type TaxSpec,
} from '@finsim/model';
import { topRate } from '../../entities/project';
import { withOptional } from '../../shared/lib/with-optional';

export { withOptional };

/**
 * Switches a formula between a percentage and a tax, keeping its sources. A percentage becomes a
 * flat tax at the same rate (as of `at`) and back; deductions and schedules are dropped.
 */
export function convertFormulaSpec(
  spec: FormulaSpec,
  kind: FormulaSpec['kind'],
  at: MonthIndex,
): FormulaSpec {
  if (spec.kind === kind) return spec;
  if (spec.kind === 'percentOf') {
    return {
      kind: 'tax',
      sources: spec.sources,
      deductions: [],
      brackets: {
        kind: 'constant',
        value: [{ from: 0, rate: Math.min(1, scheduledValueAt(spec.percent, at) ?? 0) }],
      },
      indexThresholds: 'none',
      settlement: 'monthly',
    };
  }
  return {
    kind: 'percentOf',
    sources: spec.sources,
    percent: { kind: 'constant', value: topRate(scheduledValueAt(spec.brackets, at) ?? []) },
  };
}

/**
 * Copies a preset's figures into a tax, keeping what is taxed. `null` keeps the values and only
 * drops the provenance (the rules become the user's own).
 */
export function applyTaxPreset(
  spec: TaxSpec,
  presetId: TaxPresetId | null,
  ctx: TaxPresetContext,
): TaxSpec {
  if (presetId === null) return withOptional(spec, 'presetId', undefined);
  return { ...TAX_PRESETS[presetId].build(ctx), sources: spec.sources };
}

/** A new bracket above the last one (double its threshold, same rate), keeping them ascending. */
export function nextBracket(brackets: readonly TaxBracket[]): TaxBracket {
  const last = brackets.at(-1);
  if (!last) return { from: 0, rate: 0.1 };
  return { from: last.from > 0 ? last.from * 2 : 10_000, rate: last.rate };
}

/** Brackets sorted by threshold after an edit (the first always starts at 0). */
export function sortBrackets(brackets: readonly TaxBracket[]): TaxBracket[] {
  return [...brackets].sort((a, b) => a.from - b.from);
}

/** A deduction id not used yet in `deductions`. */
export function newDeductionId(deductions: readonly { id: string }[]): string {
  const ids = new Set(deductions.map((d) => d.id));
  let n = deductions.length + 1;
  while (ids.has(`d${String(n)}`)) n++;
  return `d${String(n)}`;
}

/**
 * Source refs encoded as select values: `item:<id>`, `category:<id>`, `accountReturn:<id>`,
 * `totalIncome`, `totalExpenses`.
 */
export function encodeSource(ref: FormulaSource['ref']): string {
  return ref.type === 'totalIncome' || ref.type === 'totalExpenses'
    ? ref.type
    : `${ref.type}:${ref.id}`;
}

export function decodeSource(value: string): FormulaSource['ref'] {
  if (value === 'totalIncome' || value === 'totalExpenses') return { type: value };
  const [type, id = ''] = value.split(/:(.*)/s);
  if (type === 'category') return { type: 'category', id: id as CategoryId };
  if (type === 'accountReturn') return { type: 'accountReturn', id: id as ItemId };
  return { type: 'item', id: id as ItemId };
}

/**
 * Keeps an instalment plan valid after an edit: in month order, with the last instalment paying
 * whatever the others leave (so the shares always add up to 100 %).
 */
export function normalizePayments(payments: readonly AssetPayment[]): AssetPayment[] {
  const sorted = [...payments].sort((a, b) => a.month - b.month);
  const last = sorted.pop();
  if (!last) return [];
  const others = sorted.reduce((sum, p) => sum + p.share, 0);
  return [...sorted, { ...last, share: Math.max(0, 1 - others) }];
}

/** The plan offered when instalments are switched on: 20 % now, 80 % a year later. */
export function defaultPayments(start: number, horizonMonths: number): AssetPayment[] {
  const later = Math.min(start + 12, horizonMonths - 1);
  return normalizePayments([
    { month: start as MonthIndex, share: 0.2 },
    { month: later as MonthIndex, share: 0.8 },
  ]);
}

/** Shares move in steps of 0.01 % (the share field's precision). */
const SHARE_STEP = 0.0001;

/**
 * Adds an instalment by splitting the last one in two, half a year apart, on 0.01 % steps
 * (null if there is no month or share left to split).
 */
export function addPayment(
  payments: readonly AssetPayment[],
  horizonMonths: number,
): AssetPayment[] | null {
  const last = payments[payments.length - 1];
  if (!last || last.month + 1 > horizonMonths - 1) return null;
  const half = Math.round(last.share / 2 / SHARE_STEP) * SHARE_STEP;
  if (half < SHARE_STEP || last.share - half < SHARE_STEP / 2) return null;
  const month = Math.min(last.month + 6, horizonMonths - 1) as MonthIndex;
  return normalizePayments([
    ...payments.slice(0, -1),
    { ...last, share: half },
    { month, share: last.share - half },
  ]);
}

/** The most instalment `index` may take, in %: the last instalment, the rest, keeps 0.01 %. */
export function maxInstalmentShare(payments: readonly AssetPayment[], index: number): number {
  const others = payments.reduce(
    (sum, p, i) => (i === index || i === payments.length - 1 ? sum : sum + p.share),
    0,
  );
  // Rounded down (the tolerance absorbs float sums like 0.1 + 0.2), so the rest stays above 0.
  return Math.max(0.01, (Math.floor((1 - others) / SHARE_STEP + 1e-6) - 1) / 100);
}
